import { mapOptionSyncErrors } from '../../patterns';
import { AppointmentProposalOptionModifier } from './appointmentProposalOptionModifiers.models';

export interface OptionModifierState {
  ids: string[];
  entities: Record<string, AppointmentProposalOptionModifier>;
  selectedProposalId: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  isLoadingModifierIds: string[];
  loadedProposalIds: string[];
  error: string | null;
}

type Action =
  | {
      type: 'FETCH_OPTION_MODIFIERS';
      payload: { appointmentProposalId: string };
    }
  | {
      type: 'FETCH_OPTION_MODIFIERS_SUCCESS';
      payload: {
        optionModifiers: AppointmentProposalOptionModifier[];
        appointmentProposalId: string;
      };
    }
  | { type: 'FETCH_OPTION_MODIFIERS_FAILURE'; payload: { error: string } }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_MODIFIERS';
      payload: { appointmentProposalId: string };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_MODIFIERS_SUCCESS';
      payload: {
        optionModifiers: AppointmentProposalOptionModifier[];
        appointmentProposalId: string;
      };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_MODIFIERS_FAILURE';
      payload: { error: string };
    }
  | { type: 'CREATE_OPTION_MODIFIER'; payload: { modifierId: string } }
  | {
      type: 'CREATE_OPTION_MODIFIER_SUCCESS';
      payload: { optionModifier: AppointmentProposalOptionModifier };
    }
  | {
      type: 'CREATE_OPTION_MODIFIER_FAILURE';
      payload: { error: string; modifierId: string };
    }
  | { type: 'UPDATE_OPTION_MODIFIER' }
  | {
      type: 'UPDATE_OPTION_MODIFIER_SUCCESS';
      payload: { optionModifier: AppointmentProposalOptionModifier };
    }
  | { type: 'UPDATE_OPTION_MODIFIER_FAILURE'; payload: { error: string } }
  | { type: 'DELETE_OPTION_MODIFIER' }
  | {
      type: 'DELETE_OPTION_MODIFIER_SUCCESS';
      payload: { appointmentProposalOptionModifierId: string };
    }
  | { type: 'DELETE_OPTION_MODIFIER_FAILURE'; payload: { error: string } }
  | { type: 'CREATE_MANY_OPTION_MODIFIERS'; payload: { modifierIds: string[] } }
  | {
      type: 'CREATE_MANY_OPTION_MODIFIERS_SUCCESS';
      payload: {
        optionModifiers: AppointmentProposalOptionModifier[];
        modifierIds: string[];
      };
    }
  | {
      type: 'CREATE_MANY_OPTION_MODIFIERS_FAILURE';
      payload: { error: string; modifierIds: string[] };
    }
  | {
      type: 'UPSERT_MODIFIERS';
      payload: {
        appointmentProposalOptionModifiers: AppointmentProposalOptionModifier[];
      };
    }
  | {
      type: 'SET_MODIFIERS_MUTATING';
      payload: { creatingIds?: string[] };
    }
  | {
      type: 'CLEAR_MODIFIERS_MUTATING';
      payload: { creatingIds?: string[]; error?: string };
    }
  | {
      type: 'SELECT_PROPOSAL_MODIFIERS';
      payload: { appointmentProposalId: string };
    };

export const initState: OptionModifierState = {
  ids: [],
  entities: {},
  selectedProposalId: null,
  isLoaded: false,
  isLoading: true,
  isLoadingModifierIds: [],
  loadedProposalIds: [],
  error: null,
};

export const optionModifierReducer = (
  state: OptionModifierState,
  action: Action
): OptionModifierState => {
  switch (action.type) {
    case 'FETCH_APPOINTMENT_PROPOSAL_MODIFIERS':
    case 'FETCH_OPTION_MODIFIERS':
      return {
        ...state,
        error: null,
        isLoading: true,
        isLoaded: state.loadedProposalIds.includes(
          action.payload.appointmentProposalId
        ),
        selectedProposalId: action.payload.appointmentProposalId,
      };
    case 'FETCH_APPOINTMENT_PROPOSAL_MODIFIERS_SUCCESS':
    case 'FETCH_OPTION_MODIFIERS_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        loadedProposalIds: [
          ...new Set([
            ...state.loadedProposalIds,
            action.payload.appointmentProposalId,
          ]),
        ],
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.optionModifiers.map(
              (a) => a.appointmentProposalOptionModifierId
            ),
          ]),
        ],
        entities: action.payload.optionModifiers.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentProposalOptionModifierId]:
              mapOptionSyncErrors(curr),
          }),
          state.entities
        ),
      };

    case 'CREATE_OPTION_MODIFIER':
      return {
        ...state,
        isLoadingModifierIds: [
          ...new Set([
            ...state.isLoadingModifierIds,
            action.payload.modifierId,
          ]),
        ],
        error: null,
      };
    case 'UPDATE_OPTION_MODIFIER':
    case 'DELETE_OPTION_MODIFIER':
      return { ...state, isLoading: true, error: null };
    case 'CREATE_OPTION_MODIFIER_SUCCESS':
    case 'UPDATE_OPTION_MODIFIER_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoadingModifierIds: state.isLoadingModifierIds.filter(
          (id) => id !== action.payload.optionModifier.discountsAndFeesId
        ),
        ids: [
          ...new Set([
            ...state.ids,
            action.payload.optionModifier.appointmentProposalOptionModifierId,
          ]),
        ],
        entities: {
          ...state.entities,
          [action.payload.optionModifier.appointmentProposalOptionModifierId]:
            mapOptionSyncErrors(action.payload.optionModifier),
        },
      };
    case 'DELETE_OPTION_MODIFIER_SUCCESS':
      return {
        ...state,
        isLoading: false,
        ids: state.ids.filter(
          (id) => id !== action.payload.appointmentProposalOptionModifierId
        ),
      };
    case 'CREATE_MANY_OPTION_MODIFIERS':
      return {
        ...state,
        isLoadingModifierIds: [
          ...new Set([
            ...state.isLoadingModifierIds,
            ...action.payload.modifierIds,
          ]),
        ],
        error: null,
      };
    case 'CREATE_MANY_OPTION_MODIFIERS_SUCCESS':
      return {
        ...state,
        isLoadingModifierIds: state.isLoadingModifierIds.filter(
          (id) => !action.payload.modifierIds.includes(id)
        ),
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.optionModifiers.map(
              (m) => m.appointmentProposalOptionModifierId
            ),
          ]),
        ],
        entities: action.payload.optionModifiers.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.appointmentProposalOptionModifierId]:
              mapOptionSyncErrors(curr),
          }),
          state.entities
        ),
      };
    case 'CREATE_MANY_OPTION_MODIFIERS_FAILURE':
      return {
        ...state,
        isLoadingModifierIds: state.isLoadingModifierIds.filter(
          (id) => !action.payload.modifierIds.includes(id)
        ),
        error: action.payload.error,
      };

    case 'CREATE_OPTION_MODIFIER_FAILURE':
      return {
        ...state,
        isLoadingModifierIds: state.isLoadingModifierIds.filter(
          (id) => id !== action.payload.modifierId
        ),
        error: action.payload.error,
      };

    case 'FETCH_APPOINTMENT_PROPOSAL_MODIFIERS_FAILURE':
    case 'FETCH_OPTION_MODIFIERS_FAILURE':
    case 'UPDATE_OPTION_MODIFIER_FAILURE':
    case 'DELETE_OPTION_MODIFIER_FAILURE':
      return { ...state, isLoading: false, error: action.payload.error };

    case 'SELECT_PROPOSAL_MODIFIERS': {
      const isLoaded = state.loadedProposalIds.includes(
        action.payload.appointmentProposalId
      );
      return {
        ...state,
        error: null,
        isLoading: !isLoaded,
        isLoaded: isLoaded,
        selectedProposalId: action.payload.appointmentProposalId,
      };
    }

    case 'UPSERT_MODIFIERS': {
      const modifiers = action.payload.appointmentProposalOptionModifiers;
      const arrivedModifierIds = modifiers.map((m) => m.discountsAndFeesId);
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        isLoadingModifierIds: state.isLoadingModifierIds.filter(
          (id) => !arrivedModifierIds.includes(id)
        ),
        loadedProposalIds: [
          ...new Set([
            ...state.loadedProposalIds,
            ...modifiers.map((m) => m.appointmentProposalId),
          ]),
        ],
        ids: [
          ...new Set([
            ...state.ids,
            ...modifiers.map((m) => m.appointmentProposalOptionModifierId),
          ]),
        ],
        entities: modifiers.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentProposalOptionModifierId]:
              mapOptionSyncErrors(curr),
          }),
          state.entities
        ),
      };
    }

    case 'SET_MODIFIERS_MUTATING':
      return {
        ...state,
        error: null,
        isLoadingModifierIds: action.payload.creatingIds
          ? [
              ...new Set([
                ...state.isLoadingModifierIds,
                ...action.payload.creatingIds,
              ]),
            ]
          : state.isLoadingModifierIds,
        isLoading: action.payload.creatingIds ? state.isLoading : true,
      };

    case 'CLEAR_MODIFIERS_MUTATING':
      return {
        ...state,
        error: action.payload.error ?? state.error,
        isLoadingModifierIds: action.payload.creatingIds
          ? state.isLoadingModifierIds.filter(
              (id) => !action.payload.creatingIds?.includes(id)
            )
          : state.isLoadingModifierIds,
        isLoading: action.payload.creatingIds ? state.isLoading : false,
      };

    default:
      return state;
  }
};
